import {
  db,
  type LocalCategory,
  type LocalMenuItem,
  type LocalVariant,
  type LocalModifierGroup,
  type LocalModifier,
} from "./db";
import { money, type Money } from "../../domain/money/money";
import { catalogSchema } from "../schemas";

/**
 * Catalog replication and the in-memory read index.
 *
 * The menu grid reads from `catalogIndex`, never from IndexedDB and never from
 * the network. Principle V allows no I/O between tapping an item and the cart
 * updating — an IndexedDB round trip is small, but it is asynchronous, and at
 * that point the 100 ms budget is being spent on plumbing rather than paint.
 */

export interface CatalogItemView {
  item: LocalMenuItem;
  variants: LocalVariant[];
  defaultVariant: LocalVariant | null;
  modifierGroups: Array<{ group: LocalModifierGroup; modifiers: LocalModifier[] }>;
}

export interface CatalogIndex {
  categories: LocalCategory[];
  /** Top-level categories, in sort order — one tab each. */
  tabs: LocalCategory[];
  /** Sections under a tab, keyed by the tab's id. Absent means no sections. */
  sectionsByTab: Map<string, LocalCategory[]>;
  itemsByCategory: Map<string, CatalogItemView[]>;
  itemsById: Map<string, CatalogItemView>;
  variantsById: Map<string, LocalVariant>;
  modifiersById: Map<string, LocalModifier>;
}

let index: CatalogIndex | null = null;

/** Replace the local catalog wholesale from a server payload. */
export async function hydrateCatalog(payload: unknown): Promise<void> {
  const parsed = catalogSchema.parse(payload);

  const toMoney = (value: number): Money => money(value);

  await db.transaction(
    "rw",
    [db.categories, db.menuItems, db.variants, db.modifierGroups, db.modifiers],
    async () => {
      await Promise.all([
        db.categories.clear(),
        db.menuItems.clear(),
        db.variants.clear(),
        db.modifierGroups.clear(),
        db.modifiers.clear(),
      ]);

      await db.categories.bulkAdd(parsed.categories);
      await db.menuItems.bulkAdd(parsed.menuItems);
      await db.variants.bulkAdd(parsed.variants.map((v) => ({ ...v, price: toMoney(v.price) })));
      await db.modifierGroups.bulkAdd(parsed.modifierGroups);
      await db.modifiers.bulkAdd(
        parsed.modifiers.map((m) => ({ ...m, priceAdjustment: toMoney(m.priceAdjustment) })),
      );
    },
  );

  index = null;
}

/** Build the in-memory index from the local replica. Call once on till load. */
export async function loadCatalogIndex(): Promise<CatalogIndex> {
  const [categories, items, variants, groups, modifiers] = await Promise.all([
    db.categories.orderBy("sortOrder").toArray(),
    db.menuItems.orderBy("sortOrder").toArray(),
    db.variants.toArray(),
    db.modifierGroups.toArray(),
    db.modifiers.toArray(),
  ]);

  const variantsByItem = new Map<string, LocalVariant[]>();
  for (const variant of variants) {
    const list = variantsByItem.get(variant.menuItemId) ?? [];
    list.push(variant);
    variantsByItem.set(variant.menuItemId, list);
  }
  for (const list of variantsByItem.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);

  const modifiersByGroup = new Map<string, LocalModifier[]>();
  for (const modifier of modifiers) {
    const list = modifiersByGroup.get(modifier.modifierGroupId) ?? [];
    list.push(modifier);
    modifiersByGroup.set(modifier.modifierGroupId, list);
  }
  for (const list of modifiersByGroup.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);

  const groupsByItem = new Map<string, LocalModifierGroup[]>();
  for (const group of groups) {
    const list = groupsByItem.get(group.menuItemId) ?? [];
    list.push(group);
    groupsByItem.set(group.menuItemId, list);
  }
  for (const list of groupsByItem.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);

  const itemsByCategory = new Map<string, CatalogItemView[]>();
  const itemsById = new Map<string, CatalogItemView>();

  for (const item of items) {
    const itemVariants = variantsByItem.get(item.id) ?? [];
    const view: CatalogItemView = {
      item,
      variants: itemVariants,
      defaultVariant: itemVariants.find((v) => v.isDefault) ?? itemVariants[0] ?? null,
      modifierGroups: (groupsByItem.get(item.id) ?? []).map((group) => ({
        group,
        modifiers: modifiersByGroup.get(group.id) ?? [],
      })),
    };

    itemsById.set(item.id, view);
    const list = itemsByCategory.get(item.categoryId) ?? [];
    list.push(view);
    itemsByCategory.set(item.categoryId, list);
  }

  /*
   * Two levels, resolved once here rather than on every render.
   *
   * A category whose parentId names a category that is absent — deactivated,
   * or not yet replicated — is promoted to a tab instead of being dropped.
   * Losing a whole section of the menu because its parent went missing would
   * make those items unsellable, which is a far worse failure than showing an
   * unexpected extra tab.
   */
  const byId = new Map(categories.map((c) => [c.id, c]));
  const tabs: LocalCategory[] = [];
  const sectionsByTab = new Map<string, LocalCategory[]>();

  for (const category of categories) {
    const parent = category.parentId ? byId.get(category.parentId) : undefined;
    if (!parent) {
      tabs.push(category);
      continue;
    }
    const list = sectionsByTab.get(parent.id) ?? [];
    list.push(category);
    sectionsByTab.set(parent.id, list);
  }

  index = {
    categories,
    tabs,
    sectionsByTab,
    itemsByCategory,
    itemsById,
    variantsById: new Map(variants.map((v) => [v.id, v])),
    modifiersById: new Map(modifiers.map((m) => [m.id, m])),
  };

  return index;
}

export function getCatalogIndex(): CatalogIndex | null {
  return index;
}

/**
 * Does an item need a sheet, or can it go straight into the cart?
 *
 * One-tap add is FR-008, and it is the difference between a fast till and a
 * tedious one. An item only interrupts when there is a genuine choice to make.
 */
export function needsConfiguration(view: CatalogItemView): boolean {
  if (view.variants.length > 1) return true;
  return view.modifierGroups.some(({ group }) => group.required || group.minSelections > 0);
}
